import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ArrowRight, 
  ArrowUp,
  CheckCircle, 
  Users, 
  Award, 
  TrendingUp,
  Globe,
  Smartphone,
  Building2,
  Link,
  Cloud,
  Wrench,
  Star,
  Phone,
  Mail,
  MapPin,
  Menu,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import AISecuritySection from '../components/AISecuritySection';
import {
  trainingPrograms,
  customSoftwareServices,
  portfolioProjects,
  testimonials,
  pricingPlans,
  softwarePricing,
  stats,
  faqs
} from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Users,
  CheckCircle,
  Award,
  TrendingUp,
  Globe,
  Smartphone,
  Building2,
  Link,
  Cloud,
  Wrench
};

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = 'Sikka IT Solutions | AI-Powered Custom Software Development in Lucknow';
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      
      toast.success("Thank you for your interest!", {
        description: "We'll get back to you within 24 hours.",
      });
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error("Something went wrong!", {
        description: "Please try again or contact us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div>
                <img 
                  src={require('../assets/sikka-logo.jpg')} 
                  alt="Sikka IT Solutions" 
                  className="h-10 sm:h-12 w-auto object-contain"
                />
              </div>
            </div>
            <nav className="hidden md:flex space-x-6">
              <a href="#services" className="text-slate-700 hover:text-blue-600 transition-colors">Services</a>
              <a href="#portfolio" className="text-slate-700 hover:text-blue-600 transition-colors">Portfolio</a>
              <a href="#pricing" className="text-slate-700 hover:text-blue-600 transition-colors">Pricing</a>
              <a href="/careers" className="text-slate-700 hover:text-blue-600 transition-colors">Careers</a>
              <a href="#contact" className="text-slate-700 hover:text-blue-600 transition-colors">Contact</a>
            </nav>
            <div className="flex items-center gap-3">
              <Button className="hidden sm:flex bg-blue-600 hover:bg-blue-700" onClick={() => window.location.href = '#contact'}>
                Get Started
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <button
                data-testid="mobile-menu-toggle"
                className="md:hidden p-2 text-slate-700 hover:text-blue-600"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
          {mobileMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 border-t border-slate-200 pt-4 flex flex-col space-y-3" data-testid="mobile-menu">
              <a href="#services" className="text-slate-700 hover:text-blue-600 transition-colors" onClick={() => setMobileMenuOpen(false)}>Services</a>
              <a href="#portfolio" className="text-slate-700 hover:text-blue-600 transition-colors" onClick={() => setMobileMenuOpen(false)}>Portfolio</a>
              <a href="#pricing" className="text-slate-700 hover:text-blue-600 transition-colors" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
              <a href="/careers" className="text-slate-700 hover:text-blue-600 transition-colors" onClick={() => setMobileMenuOpen(false)}>Careers</a>
              <a href="#contact" className="text-slate-700 hover:text-blue-600 transition-colors" onClick={() => setMobileMenuOpen(false)}>Contact</a>
              <Button className="bg-blue-600 hover:bg-blue-700 w-full" onClick={() => { setMobileMenuOpen(false); window.location.href = '#contact'; }}>
                Get Started <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden bg-gradient-to-br from-blue-50 via-cyan-50 to-slate-50 dark:from-slate-800 dark:via-slate-900 dark:to-slate-800 transition-colors duration-300">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-cyan-50 to-slate-50 opacity-70"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-200">
               Make your Professional IT Software today 
              </Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
                Transform Your
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500"> Future </span>
                with our Expertise  
              </h1>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                We deliver results faster by combining AI-native engineers with intelligent internal agents that work in parallel—dramatically shortening the journey from problem definition to a production-ready system. Join 100+ businesses who trust Sikka IT Solutions and more than 50,000 customers who trust Sikka Broadband Pvt. Ltd.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700" onClick={() => window.location.href = '#contact'}>
                  Enquire Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                {/* <Button size="lg" variant="outline" className="border-slate-300 hover:bg-slate-50" onClick={() => window.location.href = '/other-business-solutions'}>
                  View Other Business Solutions
                </Button> */}
              </div>
            </div>
            <div className="relative mt-8 md:mt-0">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={require('../assets/office-team.jpg')} 
                  alt="Sikka IT Solutions team at work" 
                  className="w-full h-auto"
                />
              </div>
              <div className="hidden md:block absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border border-slate-100">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">95%</p>
                    <p className="text-sm text-slate-600">Success Rate</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-y border-slate-200">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = iconMap[stat.icon];
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-3">
                    <Icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <p className="text-3xl font-bold text-slate-900 mb-1">{stat.value}</p>
                  <p className="text-sm text-slate-600">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Other Ventures Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700">Sikka- A name you can trust</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Our Other Ventures
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              With 30+ years of experience and more than 50,000 customers, we deliver excellence across multiple industries including 
              broadband internet services, Cable Tv, OTT plans and premium real estate flats for better living in Lucknow
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="group hover:shadow-2xl transition-all duration-300 border-slate-200 overflow-hidden">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://www.sikkanet.com/assets/img/hero/1.jpg" 
                  alt="Sikka Broadband" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-2xl font-bold text-white mb-1">Sikka Broadband</h3>
                  <p className="text-sm text-white/90">High-Speed Internet Service Provider</p>
                </div>
              </div>
              <CardContent className="pt-6">
                <p className="text-slate-600 mb-4">
                  Award-winning ISP providing fiber internet, leased lines, cable TV, and enterprise networking solutions 
                  across Lucknow. Serving 30,000+ satisfied customers.
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge variant="outline" className="bg-blue-50">Fiber Internet</Badge>
                  <Badge variant="outline" className="bg-blue-50">Leased Line</Badge>
                  <Badge variant="outline" className="bg-blue-50">Cable TV</Badge>
                  <Badge variant="outline" className="bg-blue-50">Enterprise</Badge>
                </div>
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  onClick={() => window.open('https://www.sikkanet.com', '_blank')}
                >
                  Visit Website
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-2xl transition-all duration-300 border-slate-200 overflow-hidden">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://www.sikkainfratech.com/assets/images/slider/1.jpg" 
                  alt="Sikka Infratech" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-2xl font-bold text-white mb-1">Sikka Infratech</h3>
                  <p className="text-sm text-white/90">Premium Real Estate Development</p>
                </div>
              </div>
              <CardContent className="pt-6">
                <p className="text-slate-600 mb-4">
                  Luxury residential projects combining modern architecture with natural tranquility. Currently developing 
                  Sikka Riverside Residency - a premium riverside living experience.
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge variant="outline" className="bg-purple-50">Luxury Apartments</Badge>
                  <Badge variant="outline" className="bg-purple-50">Penthouses</Badge>
                  <Badge variant="outline" className="bg-purple-50">RERA Approved</Badge>
                </div>
                <Button 
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  onClick={() => window.open('https://www.sikkainfratech.com', '_blank')}
                >
                  Visit Website
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* <div className="text-center mt-12">
            <Button 
              size="lg" 
              variant="outline" 
              className="border-slate-300 hover:bg-slate-50"
              onClick={() => window.location.href = '/other-business-solutions'}
            >
              Learn More About Our Ventures
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div> */}
        </div>
      </section>

      {/* AI Security & Why We Succeed Section */}
      <AISecuritySection />

     
      {/* Training Services */}
       {/* <section id="training" className="py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-cyan-100 text-cyan-700">Our technologies</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Transform your business with competitive Software solutions tailored to your needs and budget
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              In today’s market, your digital presence is often the first thing a customer sees.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {trainingPrograms.map((program) => (
              <Card key={program.id} className="group hover:shadow-xl transition-all duration-300 border-slate-200">
                <div className="relative h-48 overflow-hidden rounded-t-lg">
                  <img 
                    src={program.image} 
                    alt={program.title} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-white/90 text-slate-900">{program.level}</Badge>
                  </div>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl">{program.title}</CardTitle>
                  <CardDescription className="text-slate-600">{program.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-4 text-sm text-slate-600">
                    <span>Duration: {program.duration}</span>
                  </div>
                  <div className="space-y-2 mb-4">
                    {program.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center text-sm text-slate-700">
                        <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                        {feature}
                      </div>
                    ))}
                  </div>
                  <Button className="w-full bg-blue-600 hover:bg-blue-700">
                    Enroll Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>  */}
      

      {/* Custom Software Services */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-purple-100 text-purple-700">Custom Software Development</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Tailored Solutions for Your Business
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From concept to deployment, we build custom software that drives your business forward
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {customSoftwareServices.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="hover:shadow-lg transition-shadow border-slate-200">
                  <CardHeader>
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <CardTitle className="text-lg">{service.title}</CardTitle>
                    <CardDescription className="text-slate-600">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-slate-700">Technologies:</p>
                      <div className="flex flex-wrap gap-2">
                        {service.technologies.map((tech, idx) => (
                          <Badge key={idx} variant="outline" className="bg-slate-50 text-xs">
                            {tech}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Services Banner */}
          <div className="mt-12">
            <a
              href="#contact"
              data-testid="services-banner-link"
              className="block rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300 cursor-pointer"
            >
              <img
                src={require('../assets/services-banner.png')}
                alt="Your Vision, Our Code - Sikka IT Solutions Services"
                className="w-full h-auto"
              />
            </a>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700">Our Work</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Recent Projects
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              See how we've helped businesses transform their operations with innovative software solutions
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {portfolioProjects.map((project) => (
              <Card key={project.id} className="overflow-hidden hover:shadow-xl transition-shadow border-slate-200">
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={project.title} 
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                  <Badge className="absolute top-4 left-4 bg-white/90 text-slate-900">
                    {project.category}
                  </Badge>
                </div>
                <CardHeader>
                  <CardTitle className="text-xl">{project.title}</CardTitle>
                  <p className="text-sm text-slate-500">{project.client}</p>
                  <CardDescription className="text-slate-600 mt-2">{project.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech, idx) => (
                      <Badge key={idx} variant="outline" className="bg-slate-50">
                        {tech}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-amber-100 text-amber-700">Testimonials</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Don't just take our word for it - hear from those who've experienced our training and services
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-6 italic">"{testimonial.text}"</p>
                  <div className="flex items-center">
                    <div>
                      <p className="font-semibold text-slate-900">{testimonial.name}</p>
                      <p className="text-sm text-slate-600">{testimonial.role} at {testimonial.company}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-indigo-100 text-indigo-700">Pricing Plans</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Invest in Your Future
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Flexible pricing options for individuals and businesses
            </p>
          </div>

          {/* <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">Training Programs</h3>
            <div className="grid md:grid-cols-3 gap-8">
              {pricingPlans.map((plan) => (
                <Card 
                  key={plan.id} 
                  className={`relative ${plan.popular ? 'border-blue-500 border-2 shadow-xl' : 'border-slate-200'}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-blue-600 text-white">Most Popular</Badge>
                    </div>
                  )}
                  <CardHeader className="text-center pb-8">
                    <CardTitle className="text-2xl mb-2">{plan.name}</CardTitle>
                    <p className="text-sm text-slate-600 mb-4">{plan.description}</p>
                    <div>
                      <span className="text-4xl font-bold text-slate-900">{plan.price}</span>
                      <span className="text-slate-600 ml-2">/ {plan.period}</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-sm text-slate-700">
                          <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button 
                      className={`w-full ${plan.popular ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-900 hover:bg-slate-800'}`}
                    >
                      Get Started
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div> */}

          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">Software Development</h3>
            <div className="grid md:grid-cols-3 gap-8">
              {softwarePricing.map((plan) => (
                <Card key={plan.id} className="border-slate-200">
                  <CardHeader className="text-center pb-8">
                    <CardTitle className="text-2xl mb-2">{plan.name}</CardTitle>
                    <p className="text-sm text-slate-600 mb-4">{plan.description}</p>
                    <div>
                      <span className="text-4xl font-bold text-slate-900">{plan.price}</span>
                      {plan.period && <span className="text-slate-600 ml-2 text-sm">/ {plan.period}</span>}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-sm text-slate-700">
                          <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button className="w-full bg-slate-900 hover:bg-slate-800" onClick={() => window.location.href = '#contact'}>
                      Request Quote
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-rose-100 text-rose-700">FAQ</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-slate-600">
              Find answers to common questions about our services
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                <AccordionTrigger className="text-left text-slate-900 hover:text-blue-600">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <Badge className="mb-4 bg-blue-600 text-white">Get in Touch</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Start Your Journey?
              </h2>
              <p className="text-slate-300 mb-8 text-lg">
                Fill out the form and our team will get back to you within 24 hours. Let's build something amazing together!
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <Mail className="h-5 w-5 text-blue-400 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Email</p>
                    <p className="text-slate-300">raunak@sikkanet.com</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <MapPin className="h-5 w-5 text-blue-400 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Office</p>
                    <p className="text-slate-300">Sikka House, 6 La Place, Hazratganj, Lucknow- 226001</p>
                  </div>
                </div>
              </div>
            </div>

            <Card className="bg-white text-slate-900">
              <CardHeader>
                <CardTitle>Send us a message</CardTitle>
                <CardDescription>We'll respond as soon as possible</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Input 
                      name="name"
                      placeholder="Your Name" 
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <Input 
                      name="email"
                      type="email" 
                      placeholder="Your Email" 
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <Input 
                      name="phone"
                      type="tel" 
                      placeholder="Phone Number" 
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <select 
                      name="service"
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.service}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Service Interest</option>
                      <option value="website-development">Website Development</option>
                      <option value="app-development">App Development</option>
                      <option value="custom-software">Custom Software for Business (CRM, E-commerce, MLM or ERP)</option>
                      <option value="digital-marketing">Digital Marketing/SEO</option>
                      <option value="ai-powered-software">AI Powered Customised Agent or Software</option>
                      <option value="others">Others</option>
                    </select>
                  </div>
                  <div>
                    <Textarea 
                      name="message"
                      placeholder="Tell us about your requirements..." 
                      rows={4}
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="font-bold text-lg mb-2">Sikka IT Solutions</h3>
              <p className="text-slate-400 text-sm">
                We provide outcome-driven delivery where AI-native engineers and internal Ai agents work in parallel, accelerating development from problem definition to production through early alignment on constraints, users, data dependencies, decision flow, and success metrics.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">Custom Web Apps</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Mobile Development</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Enterprise Software</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API Development</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Cloud Solutions</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Digital Marketing</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#portfolio" className="hover:text-white transition-colors">Portfolio</a></li>
                <li><a href="#testimonials" className="hover:text-white transition-colors">Testimonials</a></li>
                <li><a href="/careers" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-slate-400 text-sm">
                © 2026 Sikka IT Solutions. All rights reserved.
              </p>
              <div className="flex space-x-6 mt-4 md:mt-0">
                <a href="/privacy-policy" className="text-slate-400 hover:text-white transition-colors text-sm">Privacy Policy</a>
                <a href="/terms-of-service" className="text-slate-400 hover:text-white transition-colors text-sm">Terms of Service</a>
              </div>
            </div>
          </div>

          {/* Back to Top Button */}
          <div className="flex justify-center mt-8">
            <button
              data-testid="back-to-top-btn"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="group flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 transition-all duration-300"
            >
              <ArrowUp className="h-4 w-4 group-hover:-translate-y-0.5 transition-transform" />
              <span className="text-sm">Back to Top</span>
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
